import qs from 'qs'
import request from 'api/requestUtils'
import {
  experienceMap,
  experienceAvailabilityTimesMap,
  experienceAvailabilityDatesMap,
  experiencePickupPointMap,
  experienceCustomerFieldMap,
  experienceVoucherMap,
  experienceCategoryMap,
  experienceTransferTimesMap,
  thingsToKnowSplitted,
} from './mappers/experienceMap'
import type { Experiences as ExperiencesContract, operations } from '@luxuryescapes/contract-svc-experience'
import { arrayToObject, partitionBy, without } from 'lib/array/arrayUtils'
import { isFulfilled } from 'lib/promise/promiseUtils'
import { AnythingCategory, EXPERIENCE_ANY_DATE, EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID } from 'constants/experience'
import { getFullOrder } from './order'
import { experienceTransferItemMap } from './mappers/orderMap'
import type { ApiOrderExperienceItem } from './mappers/orderMap'
import { buildAirportTransferPayload } from 'components/Pages/MyEscapesPage/AirportTransfers/utils/airportTransferUtils'
import type { UpdateAirportTransferForm, UpdateAirportTransferFormFields } from 'components/Pages/MyEscapesPage/AirportTransfers/types'
import type { ExperienceCoordinates } from 'lib/experiences/experienceUtils'

interface GetExperiencesFilters {
  latitude?: number
  longitude?: number
  categoryCodes?: Array<number>
  distanceRange?: Array<string>
  categoryCodesNotIn?: Array<number>
  from?: string
  to?: string
  postPurchaseOnly?: boolean
  hotelOfferId?: string
  showUnlisted?: boolean
  limit?: number
  offset?: number
  targetAudienceIn?: ExperiencesContract.Offer['targetAudiences']
  onlyCuratedByRegion?: boolean
  hasMemberPrice?: boolean
  experienceIds?: Array<string>
  experienceIdsNotIn?: Array<string>
  bundledPackageId?: string
  isLoyaltyEligible?: boolean
  tagIds?: Array<string>
  providerNotIn?: Array<string>
  vendor?: string
  curationData?: boolean
  includeFullCopy?: boolean
}

interface GetExperiencesParams {
  currentCurrency: string
  currentRegionCode: string
  locale?: string
  filters: GetExperiencesFilters
  airports?: Array<App.Airport>
}

export interface UpdateAirportTransferPayload {
  op: string
  data: {
    adults: number;
    boosterSeats: number;
    children: number;
    childSeats: number;
    day: string;
    fromLocationName?: string;
    fromLocationId?: string;
    toLocationName?: string;
    toLocationId?: string;
    flightNumber: string;
    specialRequests: string;
    time: string;
    bags: number;
    oversizedBags: number;
    surchargeAUD?: number;
    surchargeIDR?: number;
  }
}

export interface ExperienceSuggestedSearch {
  text: string
  url: string
}

export interface ExperienceSuggestedSearches {
  local: Array<ExperienceSuggestedSearch>
  worldwide: Array<ExperienceSuggestedSearch>
}

export async function getExperienceSuggestedSearches(countryCode: string | undefined, region: string): Promise<ExperienceSuggestedSearches> {
  const query = `?${qs.stringify({ countryCode, region })}`
  const response = await request.get<App.ApiResponse<ExperienceSuggestedSearches>>(`/api/search/experience/v1/suggested-searches${query}`)
  return response.result
}

type GetExperiencesQueryParams = operations['/api/experiences/offers/get']['parameters']['query']
export async function getExperiences({
  currentCurrency,
  locale,
  filters = {},
  currentRegionCode,
  airports,
}: GetExperiencesParams) {
  const { longitude, latitude, hotelOfferId } = filters
  const params: GetExperiencesQueryParams = {
    currency: currentCurrency,
    categoryIn: without(filters.categoryCodes ?? [], AnythingCategory.id).join(',') || undefined,
    categoryNotIn: filters.categoryCodesNotIn?.join(','),
    availabilityStartDate: filters.from,
    availabilityEndDate: filters.to,
    hotelOfferId: filters.hotelOfferId,
    showPostPurchase: filters.postPurchaseOnly,
    showUnlisted: filters.showUnlisted,
    // don't use default distance for hotelOfferId
    distanceRange: filters.distanceRange?.join(','),
    coordinates: undefined,
    limit: filters.limit,
    offset: filters.offset,
    targetAudienceIn: filters.targetAudienceIn?.join(','),
    onlyCuratedByRegion: filters.onlyCuratedByRegion,
    hasMemberPrice: filters.hasMemberPrice,
    idIn: filters.experienceIds?.length ? filters.experienceIds.join(',') : undefined,
    idNotIn: filters.experienceIdsNotIn?.length ? filters.experienceIdsNotIn.join(',') : undefined,
    bundledPackageId: filters.bundledPackageId,
    isLoyaltyEligible: filters.isLoyaltyEligible,
    tagIdIn: filters.tagIds?.join(','),
    providerNotIn: filters.providerNotIn?.length ? filters.providerNotIn.join(',') : undefined,
    vendor: filters.vendor,
    curationData: filters.curationData,
    includeFullCopy: filters.includeFullCopy,
  }

  if (!hotelOfferId && latitude && longitude) {
    params.coordinates = [latitude, longitude].join(',')
  }

  // Include `locale` in the query so CloudFront caches full-copy responses
  // under a distinct key for each locale. The CDN does not consistently honor
  // `Vary: Accept-Language` end-to-end.
  const query = qs.stringify({ ...params, locale })

  const options = locale ? { headers: { 'Accept-Language': locale } } : {}
  const response = await request.get<App.ApiResponse<Array<ExperiencesContract.Offer>>>(`/api/experiences/offers?${query}`, options)
  return {
    experiences: response.result.map(res => {
      return experienceMap({ experience: res, airports, currentRegionCode })
    }),
    total: response.total,
  }
}

export async function getBookingRecommendations(
  orderId: string,
  currentRegionCode: string,
): Promise<{ experiences: Array<App.ExperienceOffer> }> {
  const response = await request.get<App.ApiResponse<Array<ExperiencesContract.Offer>>>(
    `/api/experiences/booking-recommendation/${orderId}`,
    { credentials: 'include' },
  )
  return {
    experiences: response.result.map(res => experienceMap({ experience: res, currentRegionCode })),
  }
}

interface GetExperiencesByIdParams {
  currentCurrency?: string
  currentRegionCode: string
  airports?: Array<App.Airport>
  locale?: string
}

export async function getExperienceById(id: string, {
  currentCurrency,
  currentRegionCode,
  airports,
  locale,
}: GetExperiencesByIdParams) {
  // Include `locale` in the query so CloudFront caches each locale under a
  // distinct key. The CDN's `Vary: Accept-Language` is not honored end-to-end,
  // so identical URLs with different Accept-Language values would otherwise
  // collide and serve stale, wrong-language content.
  const query = qs.stringify({
    currency: currentCurrency,
    locale,
  })

  const options = locale ? { headers: { 'Accept-Language': locale } } : {}
  const response = await request.get<App.ApiResponse<ExperiencesContract.Offer>>(`/api/experiences/offers/${id}?${query}`, options)
  return experienceMap({ experience: response.result, airports, currentRegionCode })
}

type PassportExperienceTicketsByIdResponse = {
  ticketsByExperienceId: Record<string, Array<App.ExperienceItemTicket>>
  errors: Record<string, unknown>
}

export async function getExperienceThingsToKnow(
  offerId: string,
  locale: string,
  signal?: AbortSignal | null,
): Promise<Array<App.ExperienceMarkdownSplitted>> {
  // Include `locale` in the query so CloudFront caches each locale under a distinct key.
  const query = qs.stringify({ locale })
  const response = await request.get<App.ApiResponse<ExperiencesContract.ThingsToKnow>>(
    `/api/experiences/offers/${offerId}/things-to-know?${query}`,
    { headers: { 'Accept-Language': locale }, signal },
  )
  return response.result ? thingsToKnowSplitted(response.result) : []
}

interface ExperienceSelectionSession {
  sessionToken: string
  expiresAt: string
}

export async function createExperienceSelectionSession(offerId: string): Promise<ExperienceSelectionSession> {
  const response = await request.post<App.ApiResponse<ExperienceSelectionSession>, Record<string, never>>(
    `/api/experiences/offers/${offerId}/selection-session`,
    {},
    { credentials: 'include' },
  )
  return response.result
}

interface DateAvailabilityParams {
  pickupPointId?: string
  redemptionLocationId?: string
  tickets?: Array<string>
  ticketModesMap?: Record<string, string>
  orderId?: string
}

export function getExperienceDateAvailability(experienceId: string, params?: DateAvailabilityParams) {
  const query = qs.stringify({
    pickupId: params?.pickupPointId,
    redemptionLocationId: params?.redemptionLocationId,
    tickets: params?.tickets,
    ticketModesMap: params?.ticketModesMap,
    orderId: params?.orderId,
  })

  return request
    .get<App.ApiResponse<Array<ExperiencesContract.DateAvailability>>>(`/api/experiences/offers/${experienceId}/availability?${query}`)
    .then((response) => response.result.map(experienceAvailabilityDatesMap))
}

interface TimeAvailabilityParams {
  currencyCode: string
  pickupPointId?: string
  redemptionLocationId?: string
  customerId?: string
  tickets?: Array<string>
  ticketModesMap?: Record<string, string>
  date?: string
  orderId?: string
}

export function getExperienceTimeAvailability(
  experienceId: string,
  params: TimeAvailabilityParams,
) {
  const query = qs.stringify({
    currency: params.currencyCode,
    pickupId: params.pickupPointId,
    redemptionLocationId: params.redemptionLocationId,
    customerId: params.customerId,
    tickets: params.tickets,
    ticketModesMap: params.ticketModesMap,
    orderId: params.orderId,
  })

  const date = params.date ?? EXPERIENCE_ANY_DATE
  return request
    .get<App.ApiResponse<Array<ExperiencesContract.TimeGroups>>>(`/api/experiences/offers/${experienceId}/availability/${date}?${query}`, { credentials: 'include' })
    .then((response) => experienceAvailabilityTimesMap(response.result, experienceId))
}

interface TransferAvailabilityParams {
  currencyCode: string
  customerId?: string
}

export function getExperienceTransferAvailability(
  experienceId: string,
  bookingDate: string,
  params: TransferAvailabilityParams,
) {
  const query = qs.stringify({
    currency: params.currencyCode,
    customerId: params.customerId,
  })

  return request
    .get<App.ApiResponse<Array<ExperiencesContract.TimeGroups>>>(`/api/experiences/offers/${experienceId}/availability/${bookingDate}?${query}`, { credentials: 'include' })
    .then((response) => experienceTransferTimesMap(response.result, experienceId))
}

export function getExperiencePickupPoints(
  experienceId: string,
) {
  return request.get<App.ApiResponse<Array<ExperiencesContract.PickupPoint>>>(`/api/experiences/offers/${experienceId}/pickup-points`)
    .then(response => response.result.map(experiencePickupPointMap))
}

export function getExperienceCustomerFields(
  experienceId: string,
  ticketId: string,
  qantity: number,
  pickupId?: string,
  languageId?: string,
): Promise<Array<App.ExperienceFormField>> {
  const query = qs.stringify({
    pickupId,
    language: languageId,
  })
  return request.get<App.ApiResponse<Array<ExperiencesContract.BookingCustomerInfo>>>(
    `/api/experiences/offers/${experienceId}/customer-info/${ticketId}/${qantity}?${query}`,
  ).then(response => response.result.map(experienceCustomerFieldMap))
}

export type ExperienceByIdResponse = { experiences: Array<App.ExperienceOffer>, errors: Record<string, any> }

interface GetExperiencesByIdsParams {
  currentCurrency: string
  currentRegionCode: string
  airports?: Array<App.Airport>
  customerId?: string
  locale?: string
}

export function getExperiencesById(ids: Array<string>, {
  airports,
  currentCurrency,
  currentRegionCode,
  locale,
}: GetExperiencesByIdsParams): Promise<ExperienceByIdResponse> {
  return Promise.allSettled(ids.map(id =>
    getExperienceById(id, { currentCurrency, airports, currentRegionCode, locale }).catch(error => Promise.reject({ id, error }))),
  ).then((results) => {
    const [fufilled, rejected] = partitionBy(results, isFulfilled)
    return {
      experiences: fufilled.map(result => result.value),
      errors: arrayToObject(rejected, result => result.reason.id, result => result.reason.error),
    }
  })
}

export function getPassportExperienceTicketsById(ids: Array<string>, {
  airports,
  currentCurrency,
  currentRegionCode,
  customerId,
  locale,
}: GetExperiencesByIdsParams): Promise<PassportExperienceTicketsByIdResponse> {
  const query = qs.stringify({
    currency: currentCurrency,
    locale,
  })
  const options = locale ? { headers: { 'Accept-Language': locale } } : {}

  return Promise.allSettled(ids.map(id => request
    .get<App.ApiResponse<ExperiencesContract.Offer>>(`/api/experiences/offers/${id}?${query}`, options)
    .then(async(response) => {
      const experience = experienceMap({ experience: response.result, airports, currentRegionCode })
      const timeSlots = experience.isExperiencePassport ? await getExperienceTimeAvailability(experience.id, {
        currencyCode: currentCurrency,
        customerId,
        redemptionLocationId: experience.redemptionLocations[0]?.id,
      }) : []

      return {
        experienceId: experience.id,
        tickets: timeSlots.flatMap(timeSlot => timeSlot.tickets),
      }
    })
    .catch(error => Promise.reject({ id, error })),
  )).then((results) => {
    const [fufilled, rejected] = partitionBy(results, isFulfilled)
    return {
      ticketsByExperienceId: arrayToObject(fufilled, result => result.value.experienceId, result => result.value.tickets),
      errors: arrayToObject(rejected, result => result.reason.id, result => result.reason.error),
    }
  })
}

export function getExperienceCategories(locale?: string): Promise<Array<App.ExperienceItemCategory>> {
  const query = locale ? `?${qs.stringify({ locale })}` : ''
  const options = locale ? { headers: { 'Accept-Language': locale } } : {}
  return request.get<App.ApiResponse<Array<ExperiencesContract.Category>>>(`/api/experiences/categories/new${query}`, options).then(response => {
    return [
      AnythingCategory,
      ...response.result.map(experienceCategoryMap),
    ]
  })
}

interface SearchParams {
  placeId?: string
  placeIdToIgnore?: string
  region: string
  categoryCodes?: Array<number>
  sortBy?: Experiences.SortTypes
  campaigns?: Array<string>
  limit?: number
  priceGte?: number
  priceLte?: number
  /**
   * Format is "lat_lo,lng_lo,lat_hi,lng_hi",
   * where "lo" corresponds to the southwest corner of the bounding box
   * and "hi" corresponds to the northeast corner.
   */
  bounds?: string
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  leExclusive?: boolean
  collections?: Array<string>
  from?: string
  to?: string
  minRating?: number
  targetAudience?: Array<App.ExperienceTargetAudience>
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  isGift?: boolean
  coordinatesList?: Array<ExperienceCoordinates>
  timeSlots?: Array<Experiences.TimeSlotFilter>
}

export async function getExperienceSearchList(params: SearchParams): Promise<{ experienceIds: Array<string>, offersMetadata?: App.ExperienceOffersMetadata }> {
  const queryParams = qs.stringify({
    placeId: params.placeId,
    placeIdToIgnore: params.placeIdToIgnore,
    region: params.region,
    categoryValues: without(params.categoryCodes ?? [], AnythingCategory.id, EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID),
    sortBy: params.sortBy,
    campaigns: params.campaigns,
    limit: params.limit,
    priceGte: params.priceGte,
    priceLte: params.priceLte,
    bounds: params.bounds,
    luxPlusExclusive: params.luxPlusFeatures,
    leExclusive: params.leExclusive || undefined,
    collections: params.collections,
    ratingRange: params.minRating ? [params.minRating, 10] : undefined,
    targetAudience: params.targetAudience,
    hasFreeCancellation: params.hasFreeCancellation || undefined,
    hasInstantConfirmation: params.hasInstantConfirmation || undefined,
    availableFrom: params.from,
    availableTo: params.to,
    giftingAvailable: params.isGift || undefined,
    coordinatesList: JSON.stringify(params.coordinatesList),
    timeSlots: params.timeSlots?.join(',') || undefined,
  }, { arrayFormat: 'comma' })

  try {
    const response = await request.get<App.ApiResponse<{ ids: Array<string>, offerMetadata: App.ExperienceOffersMetadata }>>(`/api/search/experience/v1/list-enriched?${queryParams}`)
    return { experienceIds: response.result.ids, offersMetadata: response.result.offerMetadata }
  } catch(error) {
    if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
      const response = await request.get<App.ApiResponse<Array<string>>>(`/api/search/experience/v1/list?${queryParams}`)
      return { experienceIds: response.result }
    }
    throw error
  }
}

interface FacetResult {
  category: Array<{
    label: string
    value: string
    level: number
    count: number
    relevanceScore?: number
  }>
  collection: Array<{
    collectionId: string
    theme: string
    count: number
  }>
  areas?: Array<{
    placeId: string
    name: string
    nameFull: string
    type: string
    count: number
    distance: number
  }>
  price: {
    min: number
    max: number
  }
  leExclusiveCount: number
  freeCancellationCount: number
  instantConfirmationCount: number
  giftingAvailableCount: number
  luxPlusMemberPriceCount?: number
  ratingCounts: Array<{
    threshold: number
    count: number
  }>
  targetAudienceCounts: Array<{
    audience: string
    count: number
  }>
  timeSlotCounts?: Array<{
    slot: string
    count: number
  }>
  dateRange?: {
    min: string | null
    max: string | null
  }
}

export interface ExperienceFacetFilters {
  availableFrom?: string
  availableTo?: string
  categoryValues?: string
  campaigns?: string
  collections?: string
  priceGte?: number
  priceLte?: number
  luxPlusExclusive?: string
  leExclusive?: boolean
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  giftingAvailable?: boolean
  targetAudience?: string
  ratingRange?: string
  timeSlots?: string
}

export function getExperienceListFacets(
  placeId: string,
  region: string,
  filters: ExperienceFacetFilters = {},
): Promise<App.ExperienceFacets> {
  const queryParams = qs.stringify({
    placeId,
    region,
    ...filters,
  }, { arrayFormat: 'comma' })
  return request.get<App.ApiResponse<FacetResult>>(`/api/search/experience/v1/facet/list?${queryParams}`).then(response => {
    const categories = response.result.category ?? []
    const collections = response.result.collection ?? []
    return {
      fetching: false,
      category: categories.map(cat => ({
        categoryId: parseInt(cat.value),
        count: cat.count,
        relevanceScore: cat.relevanceScore,
      })),
      collection: collections.map(col => ({
        collectionId: col.collectionId,
        theme: col.theme,
        count: col.count,
      })),
      areas: (response.result.areas ?? []).map(area => ({
        placeId: area.placeId,
        name: area.name,
        nameFull: area.nameFull,
        type: area.type,
        count: area.count,
        distance: area.distance,
      })),
      price: response.result.price,
      leExclusiveCount: response.result.leExclusiveCount ?? 0,
      freeCancellationCount: response.result.freeCancellationCount ?? 0,
      instantConfirmationCount: response.result.instantConfirmationCount ?? 0,
      giftingAvailableCount: response.result.giftingAvailableCount ?? 0,
      luxPlusMemberPriceCount: response.result.luxPlusMemberPriceCount ?? 0,
      ratingCounts: (response.result.ratingCounts ?? []).map(ratingCount => ({
        threshold: ratingCount.threshold,
        count: ratingCount.count,
      })),
      targetAudienceCounts: (response.result.targetAudienceCounts ?? []).map(audienceCount => ({
        audience: audienceCount.audience as App.ExperienceTargetAudience,
        count: audienceCount.count,
      })),
      timeSlotCounts: (response.result.timeSlotCounts ?? [])
        .filter((item): item is typeof item & { slot: Experiences.TimeSlotFilter } =>
          (['all-day', 'morning', 'afternoon', 'evening'] as const).includes(item.slot as Experiences.TimeSlotFilter),
        )
        .map(item => ({
          slot: item.slot as Experiences.TimeSlotFilter,
          count: item.count,
        })),
      dateRange: response.result.dateRange,
    }
  })
}

export async function getExperienceMetrics(region: string): Promise<App.ExperienceMetrics> {
  const query = new URLSearchParams({ region })
  const response = await request.get<App.ApiResponse<App.ExperienceMetrics>>(`/api/experiences/metrics?${query}`)
  return response.result
}

export async function getExperienceVoucher(experienceItemId: string): Promise<App.ExperienceVoucher> {
  return request
    .get<App.ApiResponse<ExperiencesContract.Voucher>>(
      `/api/orders/experiences/items/${experienceItemId}/voucher`,
      { credentials: 'include' },
    )
    .then((response) => experienceVoucherMap(response.result))
}

export async function getBookingDetailsByIds(id: Array<string>): Promise<Array<ExperiencesContract.BookingDetails>> {
  const response = await request.get<App.ApiResponse<Array<ExperiencesContract.BookingDetails>>>(`/api/orders/experiences/items/booking-details/${id.join()}`, { credentials: 'include' })
  return response.bookingsDetails
}

export async function getHeroesByRegion(region: string) {
  return request.get<App.ApiResponse<{ experiencesOffersIds: Array<string> }>>(`/api/experiences/region-heroes/${region}`)
    .then(response => response.result.experiencesOffersIds)
}

export async function updateAirportTransfers(
  orderId: string,
  airportTransferData: UpdateAirportTransferForm,
): Promise<App.Order> {
  const updatedAirportTransferPromises: Array<Promise<App.OrderTransferItem>> = []

  const airportToHotelItem = airportTransferData.toHotel
  const hotelToAirportItem = airportTransferData.toAirport

  if (airportToHotelItem) {
    updatedAirportTransferPromises.push(putAirportTransfers(airportToHotelItem))
  }

  if (hotelToAirportItem) {
    updatedAirportTransferPromises.push(putAirportTransfers(hotelToAirportItem))
  }

  await Promise.all(updatedAirportTransferPromises)
  return await getFullOrder(orderId)
}

export async function putAirportTransfers(
  airportTransferData: UpdateAirportTransferFormFields,
): Promise<App.OrderTransferItem> {
  const payload = buildAirportTransferPayload(airportTransferData)

  return request
    .put<App.ApiResponse<ApiOrderExperienceItem>, UpdateAirportTransferPayload>(
      `/api/orders/experiences/items/${airportTransferData.id}`,
      payload,
      { credentials: 'include' },
    )
    .then((response) => experienceTransferItemMap(response.body.result))
}

interface TransferzFlightNumberUpdatePayload {
  op: 'update_transferz_booking'
  brand: 'luxuryescapes'
  data: { flightNumber: string }
}

interface TransferzFlightNumberUpdateResponse {
  body: {
    status: number
    result: { id_experience_items: string; deal_options: { flight_number: string } }
  }
}

export async function updateTransferzFlightNumber(experienceItemId: string, flightNumber: string): Promise<void> {
  const payload: TransferzFlightNumberUpdatePayload = {
    op: 'update_transferz_booking',
    brand: 'luxuryescapes',
    data: { flightNumber },
  }
  // This status envelope uses id_experience_items, not the order mapper's id.
  // Read the saved number by force-refreshing the order after this succeeds.
  const response = await request.put<TransferzFlightNumberUpdateResponse, TransferzFlightNumberUpdatePayload>(
    `/api/orders/experiences/items/${experienceItemId}`, payload, { credentials: 'include' },
  )
  if (response.body.status !== 200) throw new Error('Unable to save flight number')
}

interface DownloadVoucherParams {
  orderId: string
  experienceItemIds: Array<string>
  experienceOfferId?: string
  downloadAllOfferItemsVoucher?: boolean
}

export async function downloadVoucher(
  { orderId, experienceItemIds, experienceOfferId, downloadAllOfferItemsVoucher }: DownloadVoucherParams,
): Promise<Blob> {
  const query = qs.stringify({
    experienceItemIds,
    experienceOfferId,
    downloadAllOfferItemsVoucher: downloadAllOfferItemsVoucher ? 'true' : 'false',
  })
  return request.get(
    `/api/experiences/items/order/${orderId}/vouchers/download?${query}`,
    { credentials: 'include' },
  )
}

interface GetAttractionsParams {
  placeName?: string
  placeType?: string
  lePlaceId?: string
  city?: string
  country?: string
  countryCode?: string
  sort?: Experiences.AttractionSortTypes
  limit?: number
  offset?: number
}

export function getAttractions(params: GetAttractionsParams): Promise<{ attractions: Array<Experiences.Attraction>; total: number; topAttractionsLimit?: number; isCuratedDestination?: boolean }> {
  const queryParams = qs.stringify(params)
  return request.get<App.ApiResponse<Array<Experiences.Attraction>> & { topAttractionsLimit?: number; isCuratedDestination?: boolean }>(`/api/experiences/attractions?${queryParams}`)
    .then(response => ({
      attractions: response.result,
      total: response.total ?? 0,
      topAttractionsLimit: response.topAttractionsLimit,
      isCuratedDestination: response.isCuratedDestination,
    }))
}

interface GetAttractionOffersParams {
  attractionId: string
  limit?: number
  offset?: number
  categoryValues?: Array<number>
  luxPlusExclusive?: boolean
  priceLte?: number
  priceGte?: number
  availableFrom?: string
  availableTo?: string
}

export function getAttractionOffers(params: GetAttractionOffersParams): Promise<{ offers: Array<Experiences.AttractionOffer>; total: number }> {
  const { attractionId, categoryValues, luxPlusExclusive, ...rest } = params
  const queryParams = qs.stringify({
    ...rest,
    categoryValues: categoryValues?.length ? categoryValues : undefined,
    luxPlusExclusive: luxPlusExclusive || undefined,
  }, { arrayFormat: 'comma' })
  return request.get<App.ApiResponse<Array<Experiences.AttractionOffer>>>(`/api/experiences/attractions/${attractionId}/offers?${queryParams}`)
    .then(response => ({
      offers: response.result,
      total: response.total ?? 0,
    }))
}

export interface ContentTagOfferIds {
  tagId: string
  destinationId: string
  offerIds: Array<string>
}

export function getContentTagOfferIds(tagId: string, destinationId: string): Promise<ContentTagOfferIds> {
  const query = qs.stringify({ destinationId })
  return request
    .get<App.ApiResponse<ContentTagOfferIds>>(`/api/experiences/content-tags/${tagId}/offers?${query}`)
    .then(response => response.result)
}

export interface AvailableContentTag {
  id: string
  name: string
  offerCount: number
}

export function getAvailableContentTags(destinationId: string): Promise<Array<AvailableContentTag>> {
  const query = qs.stringify({ destinationId })
  return request
    .get<App.ApiResponse<Array<AvailableContentTag>>>(`/api/experiences/content-tags?${query}`)
    .then(response => response.result)
}

export type ValidateExperienceItemsPayload = operations['/api/experiences/bookings/check/post']['requestBody']['content']['application/json']
export type ValidateExperienceItemsResponse =
  operations['/api/experiences/bookings/check/post']['responses']['200']['content']['application/json'] |
  operations['/api/experiences/bookings/check/post']['responses']['207']['content']['application/json']

export interface CreateBookingReservationResponse {
  expiresAt?: string
  items: Array<{
    experienceItemId: string
    messages?: Array<{ code: string; severity: 'error' | 'warning'; text: string }>
    seatText?: string
    status: 'rejected' | 'reserved'
  }>
  reservationToken?: string
  warnings?: Array<{ text: string; ticketIndex: number }>
}

type CreateBookingReservationApiResponse =
  { result?: CreateBookingReservationResponse } & Partial<CreateBookingReservationResponse>

export function parseCreateBookingReservationResponse(
  response: CreateBookingReservationApiResponse | null | undefined,
): CreateBookingReservationResponse | undefined {
  if (!response || typeof response !== 'object') {
    return undefined
  }

  const reservation = response.result ?? response
  if (!reservation || typeof reservation !== 'object' || !Array.isArray(reservation.items)) {
    return undefined
  }

  return reservation as CreateBookingReservationResponse
}

export type CreateBookingReservationPayload = operations['/api/experiences/booking-reservations/post']['requestBody']['content']['application/json']
export type ReleaseBookingReservationPayload = operations['/api/experiences/booking-reservations/release/post']['requestBody']['content']['application/json']

export function createBookingReservation(bookingItems: CreateBookingReservationPayload['bookingItems']) {
  return request.post<CreateBookingReservationApiResponse, CreateBookingReservationPayload>(
    '/api/experiences/booking-reservations',
    { bookingItems },
    { credentials: 'include' },
  ).then(parseCreateBookingReservationResponse)
}

export function releaseBookingReservation(experienceOfferId: string, reservationToken: string) {
  const payload: ReleaseBookingReservationPayload = { experienceOfferId, reservationToken }
  return request.post('/api/experiences/booking-reservations/release', payload, { credentials: 'include' })
}

export async function validateExperiencesItems(payload: Experiences.ValidateExperienceItemsPayload) {
  return request
    .post<ValidateExperienceItemsResponse, ValidateExperienceItemsPayload>('/api/experiences/bookings/check', payload, { credentials: 'include' })
    .then((response) => {
      const successfulItemsIds = response.result ?? []

      const errors = 'errors' in response ? response.errors ?? [] : []
      const failedItems = errors.map((error) => {
        return {
          itemId: error.experienceItemId,
          message: error.message,
        }
      })

      return {
        successfulItemsIds,
        failedItems,
      }
    })
}
